import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../components/Common';
import { mensajeError } from '../lib/api';
import {
  listSesionesQuirofano,
  listAgendas,
  todayStr,
  addDaysStr,
} from '../lib/citasApi';
import ParteDelDia from '../components/quirofano/ParteDelDia';
import PdfPreviewDialog from '../components/PdfPreviewDialog';

/**
 * Quirófano — Parte del día.
 *
 * Interfaz mínima: eliges FECHA (botón "Hoy" al lado) y sale el parte de ese
 * día con todos los quirófanos y sus pacientes (alimentado por las citas de
 * las agendas de quirófano; si existieran sesiones del planificador antiguo,
 * también se muestran). En cada paciente: dar postoperatorio, recetas del
 * kit e impresión de documentos.
 */

/**
 * Pide una lista y distingue LEÍDA de NO LEÍDA. Devuelve `{ lista }` o, si el
 * servidor falla o contesta algo que no es una lista, `{ lista: null, motivo }`.
 *
 * VACÍO Y «NO SE HA PODIDO LEER» NO SON LO MISMO, tampoco un nivel por encima
 * del parte (segunda pasada de G8, 22/9/2026). Esto se pedía con un
 * `.catch(() => [])` y un `Array.isArray(x) ? x : []`: con /api/agendas caído
 * —o contestando un objeto— el parte recibía cero quirófanos y pintaba «No hay
 * quirófano programado para esa fecha», sin un solo aviso, aunque /api/citas
 * tuviera una cirugía. El `.catch` del Promise.all no llegaba a dispararse
 * nunca, porque cada promesa ya venía con el fallo tragado. Enfermería podía
 * creer que ese día no se operaba.
 *
 * El motivo se escribe con `mensajeError`, que siempre devuelve texto: el
 * `detail` de un 422 es una lista de objetos y pintarla tal cual tira la
 * página entera.
 */
function leerLista(peticion) {
  return peticion
    .then((lista) => (Array.isArray(lista)
      ? { lista }
      : { lista: null, motivo: 'el servidor ha contestado algo que no es una lista' }))
    .catch((e) => ({ lista: null, motivo: mensajeError(e, 'no se ha podido consultar') }));
}

export default function QuirofanoSesionesPage() {
  // `null` en cualquiera de las dos = NO SE HA PODIDO LEER; `[]` = ninguna.
  const [sesiones, setSesiones] = useState([]);
  const [agendas, setAgendas] = useState([]);
  const [fallosCarga, setFallosCarga] = useState({});
  const [loading, setLoading] = useState(true);
  // Se incrementa desde el botón «Reintentar» del parte para volver a pedirlo
  // todo sin desmontar el parte (así no se pierde la fecha elegida).
  const [intento, setIntento] = useState(0);
  const [pdfPreview, setPdfPreview] = useState(null); // {url, filename, title} | null

  useEffect(() => {
    const hoy = todayStr();
    let cancelled = false;
    Promise.all([
      leerLista(listSesionesQuirofano({ dateFrom: addDaysStr(hoy, -180), dateTo: addDaysStr(hoy, 180) })),
      leerLista(listAgendas()),
    ]).then(([ses, ags]) => {
      if (cancelled) return;
      setSesiones(ses.lista);
      setAgendas(ags.lista);
      setFallosCarga({ sesiones: ses.motivo, agendas: ags.motivo });
      // Se dice, además de pintarse en rojo dentro del parte.
      const rotos = [ags.motivo && 'los quirófanos', ses.motivo && 'las sesiones del planificador']
        .filter(Boolean);
      if (rotos.length) {
        toast.error(`No se han podido leer ${rotos.join(' ni ')}. Lo que ves no es lo que hay programado.`);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [intento]);

  return (
    <div>
      <PageHeader
        title="Quirófano"
        subtitle="Parte del día: pacientes y documentos de cada quirófano"
      />

      {loading ? (
        <div className="cp-card p-8 text-center text-sm text-muted-foreground">Cargando quirófanos…</div>
      ) : (
        <ParteDelDia
          sesiones={sesiones}
          agendas={agendas}
          fallosCarga={fallosCarga}
          onReintentar={() => setIntento((n) => n + 1)}
          setPreview={setPdfPreview}
        />
      )}

      <PdfPreviewDialog
        open={!!pdfPreview}
        onOpenChange={(o) => !o && setPdfPreview(null)}
        url={pdfPreview?.url}
        filename={pdfPreview?.filename}
        title={pdfPreview?.title}
      />
    </div>
  );
}
